/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, Input, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";

import { ActionInstance } from "../../../models/action-instance";
import { BaseInstance } from "../../../models/base-instance";
import { InstanceData } from "../../../models/instance-data";
import { Event } from "../../../models/event";
import { LocationInstance } from "../../../models/location-instance";
import { PropertyType, PropertyTypeUtil } from "../../../models/property-type";
import { Function } from "../../../models/function";
import { DriverObjectService } from "../../../services/driver-object.service";
import { EntityService } from "../../../services/entity.service";
import { LocationService } from "../../../services/location.service";
import { ObjectService } from "../../../services/object.service";
import { ServerObjectService } from "../../../services/server-object.service";
import { ServiceService } from "../../../services/service.service";
import { TagService } from "../../../services/tag.service";
import { ReaderService } from "../../../services/reader.service";
import { UserService } from "../../../services/user.service";
import { ZoneService } from "../../../services/zone.service";

declare var $: any;

@Component({
  selector: 'app-function-action-editor',
  templateUrl: './function-action-editor.component.html'
})
export class FunctionActionEditorComponent implements OnInit {
  
  mPropertyTypes = PropertyType;
  
  @Input()
  mAction: ActionInstance;
  
  @Input()
  mEvent: Event;  

  @Input()
  mUniqueIndex: number;

  mReferenceFunction: Function;
  mNumberOfParams: number;
  mParamIndices: number[];
  
  mFunctionType: FunctionType;
  mFunctionTypes: FunctionType[] = [FunctionType.LOCATION, FunctionType.TAG, FunctionType.READER];
  mFunctions: Function[] = [];
  mFunction: Function;

  mEventReference: EventReference; 
  mEventReferences: EventReference[];

  mReferenceType: string;
  mReferenceTypes: KeyValue[] = [
    new KeyValue('ANTENNA', 'ANTENNA_REFERENCE'),
    new KeyValue('DRIVEROBJECT', 'DRIVEROBJECT_REFERENCE'),
    new KeyValue('LOCATION', 'LOCATION_REFERENCE'),
    new KeyValue('OBJECT', 'OBJECT_REFERENCE'),
    new KeyValue('READER', 'READER_REFERENCE'),
    new KeyValue('SERVEROBJECT', 'SERVEROBJECT_REFERENCE'),
    new KeyValue('SERVICE', 'SERVICE_REFERENCE'),
    new KeyValue('TAG', 'TAG_REFERENCE'),
    new KeyValue('USER', 'USER_REFERENCE'),
    new KeyValue('ZONE', 'ZONE_REFERENCE') ];

  mInstance: BaseInstance;
  mInstances: any[];

  mLocations: KeyValue[];
  mLocation: string;
    
  mZones: KeyValue[];
  mZone: string;
    
  constructor(private route: ActivatedRoute,
    private driverObjectService: DriverObjectService,
    private entityService: EntityService,
    private locationService: LocationService,
    private objectService: ObjectService,
    private serverObjectService: ServerObjectService,
    private serviceService: ServiceService,
    private tagService: TagService,
    private readerService: ReaderService,
    private userService: UserService,
    private zoneService: ZoneService) {
  }

  get parameterLabelOne(): string {
    if (this.mAction.data != null) {
      const datum = this.mAction.data.find(d => d.property.property == 'param_1');
        
      return datum != null ? datum.value : null;
    }
    else {
      return null;
    }
  }

  get parameterValueOne(): string {
    if (this.mAction.data != null) {
      const datum = this.mAction.data.find(d => d.property.property == 'value_1');
      
      return datum != null ? datum.value : 'N/A';
    }
    else {
      return 'NULL';
    }
  }
  
  set parameterValueOne(value: string) {
    if (this.mAction.data != null) {
      const datum = this.mAction.data.find(d => d.property.property == 'value_1');

      if (datum != null) {
        datum.value = value;
        console.log('__value -> ' + value);
      }
    }
  }
  
  ngOnInit(): void {
    console.log("onInit() - Action: " + (this.mAction == null? "*NULL*" : this.mAction.action == null? "*NULL-ACTION*" : (this.mAction.action.id + "/" + this.mAction.action.name)));

    if (this.mEvent != null) {
      this.mEventReferences = [];
      for (const eventProp of this.mEvent.properties) {
        //
        // add event references is any
        if (PropertyTypeUtil.isReference(eventProp.type)) {
          const eventRef: EventReference = new EventReference();
          eventRef.name = eventProp.name;
          eventRef.value = '${' + eventProp.property + '}';
          eventRef.type = eventProp.type;
          
          this.mEventReferences.push(eventRef);
        }
      }
        
      //
      // add location and zone
      const locationRef: EventReference = new EventReference();
      locationRef.name = 'Event Location';
      locationRef.value = '${location}';
      locationRef.type = PropertyType.LOCATION_REFERENCE;
      this.mEventReferences.push(locationRef);
        
      const zoneRef: EventReference = new EventReference();
      zoneRef.name = 'Event Zone';
      zoneRef.value = '${zone}';
      zoneRef.type = PropertyType.ZONE_REFERENCE;
      this.mEventReferences.push(zoneRef);
    }
    
    //
    // create location and zone dropdowns
    this.route.data.subscribe((data: { locations: LocationInstance[] }) => {
      this.mLocations = [new KeyValue('Event Location', '${location}')];
      this.mZones = [new KeyValue('Event Zone', '${zone}')];
      
      for (const location of data.locations) {
        this.mLocations.push(new KeyValue(location.name, String(location.id)));
          
        for (const zone of location.zones) {
          this.mZones.push(new KeyValue(zone.name, String(zone.id)));
        }
      }
    });
    //Build Data for each Property that doesn't already have Data defined for it, the Data is what is modeled in the UI for editing
//    if (this.mAction.data == null) {
//      this.mAction.data = [];
//      for (const property of this.mAction.action.properties) {
//         if (!property.property.startsWith('param') && !property.property.startsWith('value')) {
//           const data = new InstanceData();
//           data.property = property;
//             
//           this.mAction.data.push(data);
//         }
//      }
//    }

    for (const property of this.mAction.action.properties) {
      if (this.mAction.data == null || this.mAction.data.find(d => d.property.property == property.property) == null) {
        const newData = new InstanceData();
        newData.property = property;
        if (this.mAction.data == null) {
          this.mAction.data = [ newData ];
        } else {
          this.mAction.data.push(newData);
        }
      }
    }
    
    //Sort the Data by their corresponding property order
    this.mAction.data.sort((data1: InstanceData, data2: InstanceData): number => {
      return data1.property.order - data2.property.order;
    });
  }

  onSelectReference() {
    this.mReferenceFunction = null;
    this.mReferenceType = null;
    this.mInstance = null;

    switch (this.mEventReference.type) {
      case PropertyType.LOCATION_REFERENCE:
//        this.mFunctionType = FunctionType.LOCATION;
        this.loadFunctions(FunctionType.LOCATION);
        
        break;
  
      case PropertyType.OBJECT_REFERENCE:
//        this.mFunctionType = FunctionType.OBJECT;
        this.loadFunctions(FunctionType.OBJECT);
        
        break;
    }
    
    //
    // reset function and parameters
    const fnName = this.mAction.data.find(d => d.property.property == 'function_name');
    if (fnName != null) {
      console.log('__________clearing fn name');
      fnName.value = '';
    }
    this.mFunction = null;
    this.syncParamValues(null);
  }

  onSelectReferenceType(): void {
    this.mReferenceFunction = null;
    this.mEventReference = null;
    this.mInstances = null;
      
    const referenceType: PropertyType = PropertyType[this.mReferenceType];
    this.loadInstances(referenceType);

  }
  
  onSelectInstance(): void {
    this.mReferenceFunction = null;
    const referenceType: PropertyType = PropertyType[this.mReferenceType];
    this.loadInstanceFunctions(this.mInstance.id, referenceType);
  }  
    
  onSelectType(type: FunctionType): void {
    console.log('Type: ' + type);
    this.mFunctionType = type;
    this.loadFunctions(type);
  }

  onSelectFunctionType(): void {
    console.log('Type: ' + this.mFunctionType);
    this.loadFunctions(this.mFunctionType); 
  }
    
  onSelectReferenceFn(): void {
    this.syncParamValues(this.mReferenceFunction);
  }
    
  onSelectFn(): void {
    this.syncParamValues(this.mFunction);
  }
  
  onSelectLocation() {
    const datum = this.mAction.data.find(d => d.property.property == 'location');
    if (datum != null) {
      datum.value = this.mLocation;
      console.log(datum.value);
    }
  } 

  onSelectZone() {
    const datum = this.mAction.data.find(d => d.property.property == 'zone');
    if (datum != null) {
      datum.value = this.mZone;
      console.log(datum.value);
    }
  } 
    
  syncParamValues(fn: Function) {
    this.mAction.data = this.mAction.data.filter(d => !d.property.property.startsWith('param'));
    this.mAction.data = this.mAction.data.filter(d => !d.property.property.startsWith('value'));
    this.mParamIndices = [];
      
    if (fn != null) {
        let i: number;
        for (i = 1; i < fn.parameters.length + 1; i++) {
          const parmProperty = this.mAction.action.properties.find(p => p.property == 'param_' + `${i}`);
          const parmData = new InstanceData();
          parmData.property = parmProperty;
          parmData.value = fn.parameters[i - 1].name;
          this.mAction.data.push(parmData);
              
          const valueProperty = this.mAction.action.properties.find(p => p.property == 'value_' + `${i}`);
          const valueData = new InstanceData();
          valueData.property = valueProperty;
          this.mAction.data.push(valueData);
            
          this.mParamIndices.push(i);
      }
        
      this.mNumberOfParams = i - 1;
    }
  }

  async loadInstances(type: PropertyType): Promise<void> {
    let instances: any[];
      
    switch (type) {
      case PropertyType.DRIVEROBJECT_REFERENCE:
        instances = await this.driverObjectService.getAllDriverObjects();
        this.mInstances = instances;
        
        break;
      case PropertyType.LOCATION_REFERENCE:
        instances = await this.locationService.getAllLocationInstances();
        this.mInstances = instances;
        
        break;
      case PropertyType.OBJECT_REFERENCE:
        instances = await this.objectService.getAllObjectInstances();
        this.mInstances = instances;
        
        break;
      case PropertyType.READER_REFERENCE:
        instances = await this.readerService.getAllReaderInstances();
        this.mInstances = instances;
        
        break;
      case PropertyType.SERVEROBJECT_REFERENCE:
        instances = await this.serverObjectService.getAllServerObjectInstances();
        this.mInstances = instances;
        
        break;
      case PropertyType.SERVICE_REFERENCE:
        instances = await this.serviceService.getAllServiceInstances();
        this.mInstances = instances;
        
        break;
      case PropertyType.TAG_REFERENCE:
        instances = await this.tagService.getAllTagInstances();
        this.mInstances = instances;
        
        break;
      case PropertyType.USER_REFERENCE:
        instances = await this.userService.getAllUsers();
        this.mInstances = instances;
        
        break;
      }
  }
    
  async loadFunctions(type: FunctionType): Promise<void> {
    let functions: Function[];

    switch (type) {
        case FunctionType.LOCATION:
            functions = await this.locationService.getAllFunctions();
            this.mFunctions = functions;
            
            break;
        case FunctionType.READER:
            functions = await this.readerService.getAllFunctions();
            this.mFunctions = functions;
            
            break;
        default:
            this.mFunctions = [];
     }
  }

  async loadInstanceFunctions(id: number, type: PropertyType): Promise<void> {
    let functions: Function[];
      
    switch (type) {
        case PropertyType.LOCATION_REFERENCE:
            functions = await this.locationService.getFunctionsForLocationInstance(id);
            this.mFunctions = functions;
            
            break;
        case PropertyType.READER_REFERENCE:
            functions = await this.readerService.getAllFunctions();
            this.mFunctions = functions;
            
            break;
        default:
            this.mFunctions = [];
     }
  }
    
}

export class EventReference {
  name: string;
  value: string;
  type: PropertyType;
}

export enum FunctionType {
  LOCATION = "LOCATION",
  OBJECT = "OBJECT",
  READER = "READER",
  TAG = "TAG"
}

export class KeyValue {
  key: string;
  value: string;
    
  constructor(key: string, value: string) {
    this.key = key;
    this.value = value;
  }
}
